const ready = (callback) => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
};

const updateCartCount = async () => {
  try {
    const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!response.ok) return null;
    const cart = await response.json();
    document.querySelectorAll("[data-david-cart-count]").forEach((node) => {
      node.textContent = String(cart.item_count || 0);
    });
    return cart;
  } catch (error) {
    console.warn("Could not refresh cart count", error);
    return null;
  }
};

const dispatchCartUpdate = (cart, source = "david-theme") => {
  if (!cart) return;
  document.dispatchEvent(
    new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: "david-header-cart",
        data: {
          itemCount: Number(cart.item_count || 0),
          source
        }
      }
    })
  );
};

const initSlider = (root) => {
  const track = root.querySelector("[data-david-slider-track]");
  const slides = [...root.querySelectorAll("[data-david-slide]")];
  const dots = [...root.querySelectorAll("[data-david-slide-dot]")];
  if (!track || slides.length < 2) return;

  let index = slides.findIndex((slide) => slide.classList.contains("is-active"));
  if (index < 0) index = 0;
  let timer = null;

  const activate = (nextIndex) => {
    index = (nextIndex + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle("is-active", slideIndex === index);
    });
    dots.forEach((dot, dotIndex) => {
      dot.setAttribute("aria-current", dotIndex === index ? "true" : "false");
    });
  };

  const start = () => {
    const delay = Number(root.dataset.autoplayMs || 9000);
    window.clearInterval(timer);
    timer = window.setInterval(() => activate(index + 1), delay);
  };

  dots.forEach((dot, dotIndex) => {
    dot.addEventListener("click", () => {
      activate(dotIndex);
      start();
    });
  });

  root.addEventListener("mouseenter", () => window.clearInterval(timer));
  root.addEventListener("mouseleave", start);
  activate(index);
  start();
};

const initTabGroups = (scope = document) => {
  scope.querySelectorAll("[data-david-tabs]").forEach((tabs) => {
    if (tabs.dataset.bound === "true") return;
    tabs.dataset.bound = "true";

    const buttons = [...tabs.querySelectorAll("[data-david-tab-button]")];
    const panels = [...tabs.querySelectorAll("[data-david-tab-panel]")];
    if (!buttons.length || !panels.length) return;

    const activate = (targetId) => {
      buttons.forEach((button) => {
        const selected = button.dataset.target === targetId;
        button.setAttribute("aria-selected", selected ? "true" : "false");
        button.classList.toggle("is-active", selected);
      });

      panels.forEach((panel) => {
        const active = panel.dataset.panel === targetId;
        panel.hidden = !active;
      });
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => activate(button.dataset.target));
    });

    activate(buttons[0].dataset.target);
  });
};

const initGallery = (scope = document) => {
  scope.querySelectorAll("[data-david-gallery]").forEach((gallery) => {
    if (gallery.dataset.bound === "true") return;
    gallery.dataset.bound = "true";

    const thumbs = [...gallery.querySelectorAll("[data-david-thumb]")];
    const items = [...gallery.querySelectorAll("[data-david-gallery-item]")];
    if (!thumbs.length || !items.length) return;

    const activate = (index) => {
      items.forEach((item, itemIndex) => item.classList.toggle("is-active", itemIndex === index));
      thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute("aria-current", thumbIndex === index ? "true" : "false");
      });
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener("click", () => activate(index));
    });

    activate(0);
  });
};

const initQuantityControls = (scope = document) => {
  scope.querySelectorAll("[data-david-qty]").forEach((wrapper) => {
    if (wrapper.dataset.bound === "true") return;
    wrapper.dataset.bound = "true";

    const input = wrapper.querySelector("input");
    const minus = wrapper.querySelector("[data-qty-minus]");
    const plus = wrapper.querySelector("[data-qty-plus]");
    if (!input) return;

    const clamp = (value) => Math.max(1, Number(value || 1));
    minus?.addEventListener("click", () => {
      input.value = String(clamp(Number(input.value) - 1));
    });
    plus?.addEventListener("click", () => {
      input.value = String(clamp(Number(input.value) + 1));
    });
    input.addEventListener("change", () => {
      input.value = String(clamp(input.value));
    });
  });
};

const initAddToCart = (scope = document) => {
  scope.querySelectorAll("form[data-david-add-to-cart]").forEach((form) => {
    if (form.dataset.bound === "true") return;
    form.dataset.bound = "true";

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const button = form.querySelector("[type='submit']");
      const labelNode = button?.querySelector("[data-david-button-label]") || button;
      const defaultLabel = labelNode?.textContent || "Add to cart";

      try {
        button?.setAttribute("disabled", "disabled");
        if (labelNode) labelNode.textContent = "Adding";

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form)
        });

        if (!response.ok) {
          throw new Error(await response.text());
        }

        if (labelNode) labelNode.textContent = "Added";
        const cart = await updateCartCount();
        dispatchCartUpdate(cart, "product-form-component");
        window.setTimeout(() => {
          if (labelNode) labelNode.textContent = defaultLabel;
          button?.removeAttribute("disabled");
        }, 1300);
      } catch (error) {
        console.warn("Add to cart failed", error);
        if (labelNode) labelNode.textContent = "Unavailable";
        window.setTimeout(() => {
          if (labelNode) labelNode.textContent = defaultLabel;
          button?.removeAttribute("disabled");
        }, 1500);
      }
    });
  });
};

const initDrawer = (scope = document) => {
  const drawer = scope.querySelector("[data-david-drawer]");
  if (!drawer || drawer.dataset.bound === "true") return;
  drawer.dataset.bound = "true";

  const openers = scope.querySelectorAll("[data-david-open-drawer]");
  const closers = drawer.querySelectorAll("[data-david-close-drawer]");

  const setOpen = (isOpen) => {
    drawer.classList.toggle("is-open", isOpen);
    document.documentElement.classList.toggle("david-drawer-open", isOpen);
  };

  openers.forEach((button) => button.addEventListener("click", () => setOpen(true)));
  closers.forEach((button) => button.addEventListener("click", () => setOpen(false)));
};

const initCartDrawer = (scope = document) => {
  scope.querySelectorAll("[data-david-cart-drawer]").forEach((drawer) => {
    if (drawer.dataset.bound === "true") return;
    drawer.dataset.bound = "true";

    const trigger = drawer.querySelector("[data-david-cart-trigger]");
    const dialog = drawer.querySelector(".cart-drawer__dialog");
    if (!trigger || !dialog) return;

    const syncUrl = (isOpen) => {
      const url = new URL(window.location.href);
      if (isOpen) {
        url.searchParams.set("modals", "cart-drawer-dialog");
      } else if (url.searchParams.get("modals") === "cart-drawer-dialog") {
        url.searchParams.delete("modals");
      }
      window.history.replaceState(window.history.state, "", url.toString());
    };

    trigger.addEventListener("click", () => {
      window.setTimeout(() => syncUrl(true), 80);
    });

    dialog.addEventListener("close", () => {
      syncUrl(false);
    });

    const shouldOpenFromUrl = new URL(window.location.href).searchParams.get("modals") === "cart-drawer-dialog";
    if (shouldOpenFromUrl && !dialog.open) {
      window.setTimeout(() => {
        trigger.click();
      }, 120);
    }
  });
};

const initModule06 = (scope = document) => {
  scope.querySelectorAll("[data-david-module-06]").forEach((module) => {
    if (module.dataset.bound === "true") return;
    module.dataset.bound = "true";

    const viewport = module.querySelector("[data-module-06-viewport]");
    const slides = [...module.querySelectorAll("[data-module-06-slide]")];
    const logicalCount = Number(module.dataset.slideCount || 0);
    if (!viewport || logicalCount < 1 || slides.length < logicalCount) return;

    const autoplayMs = Math.max(3000, Number(module.dataset.autoplayMs || 5000));
    const initialSetting = Math.max(0, Number(module.dataset.initialIndex || 0));
    const safeInitial = Math.min(initialSetting, Math.max(logicalCount - 1, 0));
    let absoluteIndex = logicalCount + safeInitial;
    let autoTimer = null;
    let settleTimer = null;

    const slideVideo = (slide) => slide?.querySelector("video") || null;

    const syncControlState = (slide, video) => {
      if (!slide || !video) return;

      const muteButton = slide.querySelector("[data-module-06-audio]");
      const pauseButton = slide.querySelector("[data-module-06-pause]");
      const mutedIcon = slide.querySelector('[data-module-06-audio] [data-state="muted"]');
      const unmutedIcon = slide.querySelector('[data-module-06-audio] [data-state="unmuted"]');
      const pauseIcon = slide.querySelector('[data-module-06-pause] [data-state="pause"]');
      const playIcon = slide.querySelector('[data-module-06-pause] [data-state="play"]');

      if (muteButton) {
        muteButton.setAttribute("aria-label", video.muted ? "Unmute video" : "Mute video");
      }
      if (mutedIcon) mutedIcon.hidden = !video.muted;
      if (unmutedIcon) unmutedIcon.hidden = video.muted;

      const isPaused = video.paused;
      if (pauseButton) {
        pauseButton.setAttribute("aria-label", isPaused ? "Play video" : "Pause video");
      }
      if (pauseIcon) pauseIcon.hidden = isPaused;
      if (playIcon) playIcon.hidden = !isPaused;
    };

    const syncVideos = () => {
      slides.forEach((slide, index) => {
        const video = slideVideo(slide);
        const isActive = index === absoluteIndex;
        slide.classList.toggle("is-playing", isActive && !!video && !video.paused);

        if (!video) return;

        if (isActive) {
          video.muted = slide.dataset.module06Muted !== "false";
          if (slide.dataset.module06Paused === "true") {
            video.pause();
          } else {
            video.play().catch(() => {});
          }
        } else {
          slide.dataset.module06Muted = "true";
          slide.dataset.module06Paused = "false";
          video.muted = true;
          video.pause();
          try {
            video.currentTime = 0;
          } catch (error) {
            console.warn("Could not reset video time", error);
          }
        }

        syncControlState(slide, video);
        slide.classList.toggle("is-playing", isActive && !video.paused);
      });
    };

    const scrollToActive = (behavior = "smooth") => {
      const activeSlide = slides[absoluteIndex];
      if (!activeSlide) return;

      slides.forEach((slide, index) => {
        slide.classList.toggle("is-active", index === absoluteIndex);
      });

      const target = activeSlide.offsetLeft - (viewport.clientWidth - activeSlide.offsetWidth) / 2;
      viewport.scrollTo({
        left: Math.max(0, target),
        behavior
      });

      syncVideos();
    };

    const recenterIfNeeded = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        if (absoluteIndex < logicalCount * 2) return;
        absoluteIndex -= logicalCount;
        scrollToActive("auto");
      }, 520);
    };

    const advance = () => {
      absoluteIndex += 1;
      scrollToActive("smooth");
      recenterIfNeeded();
    };

    const startAutoplay = () => {
      window.clearInterval(autoTimer);
      if (logicalCount < 2) return;
      autoTimer = window.setInterval(() => {
        if (module.matches(":hover")) return;
        advance();
      }, autoplayMs);
    };

    module.querySelectorAll("[data-module-06-audio]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const slide = button.closest("[data-module-06-slide]");
        const video = slideVideo(slide);
        if (!slide || !video || !slide.classList.contains("is-active")) return;

        video.muted = !video.muted;
        slide.dataset.module06Muted = video.muted ? "true" : "false";
        syncControlState(slide, video);
      });
    });

    module.querySelectorAll("[data-module-06-pause]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const slide = button.closest("[data-module-06-slide]");
        const video = slideVideo(slide);
        if (!slide || !video || !slide.classList.contains("is-active")) return;

        const nextPaused = slide.dataset.module06Paused !== "true";
        slide.dataset.module06Paused = nextPaused ? "true" : "false";

        if (nextPaused) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }

        slide.classList.toggle("is-playing", !video.paused);
        syncControlState(slide, video);
      });
    });

    module.addEventListener("mouseenter", () => {
      window.clearInterval(autoTimer);
    });

    module.addEventListener("mouseleave", () => {
      startAutoplay();
    });

    window.addEventListener("resize", () => {
      scrollToActive("auto");
    });

    scrollToActive("auto");
    startAutoplay();
  });
};

const init = (scope = document) => {
  scope.querySelectorAll("[data-david-slider]").forEach((slider) => {
    if (slider.dataset.bound === "true") return;
    slider.dataset.bound = "true";
    initSlider(slider);
  });
  initTabGroups(scope);
  initGallery(scope);
  initQuantityControls(scope);
  initAddToCart(scope);
  initDrawer(scope);
  initCartDrawer(scope);
  initModule06(scope);
};

ready(() => {
  init(document);
  updateCartCount();
});

document.addEventListener("shopify:section:load", (event) => {
  init(event.target);
});
